// Pure row-building for the DivisionPage "Table" (bulk score entry) view
// (owner 2026-10-09). No React — carries the sort + name/label/date
// resolution contract under unit test. The component maps these rows to
// shadcn <Table> rows and reuses the gameStore `updateGame` save path.
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';

// Minimal getObjId (mirrors @/utils/helpers.getObjId) so this stays a
// dependency-light pure module: a ref may be a populated object, a bare
// ObjectId/string, or absent.
function objId(ref: any): string {
  if (!ref) return '';
  if (typeof ref === 'object') return ref._id ? String(ref._id) : '';
  return String(ref);
}

// Resolve a team side to a display name — same rule as DivisionPage.getTeamName
// (found team → display name [handles zqtm auto-names], else placeholder, else TBD).
function resolveName(teamRef: any, teams: any[], placeholder?: string): string {
  const id = objId(teamRef);
  if (!id) return placeholder || 'TBD';
  const found = (teams || []).find((t) => String(t._id) === id);
  return found ? getTeamDisplayName(found) : (placeholder || 'TBD');
}

// A game is cancelled when the flag is boolean true OR the store's remapped
// 'YES' string.
export function isGameCancelled(game: any): boolean {
  return game?.isCancelled === true || game?.isCancelled === 'YES';
}

// Compact round/label for the first column: explicit gameLabel wins, else
// derive from competitionMeta (playoff slot/stage, or pool + round + game).
export function gameRowLabel(game: any): string {
  if (game?.gameLabel && String(game.gameLabel).trim()) return String(game.gameLabel).trim();
  const meta = game?.competitionMeta || {};
  const slot = meta.bracketSlot || meta.stage;
  if (slot) {
    return String(slot)
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  }
  const parts: string[] = [];
  if (meta.pool) parts.push(`Pool ${meta.pool}`);
  if (meta.round != null) parts.push(`R${meta.round}`);
  if (meta.gameNumber != null) parts.push(`G${meta.gameNumber}`);
  return parts.join(' · ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Display date: "Oct 2" from a MM-DD-YYYY globalDate; falls back to the raw
// string (or '' when absent). No timezone math — globalDate is already local.
export function formatRowDate(globalDate?: string): string {
  if (!globalDate) return '';
  const m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(String(globalDate).trim());
  if (!m) return String(globalDate);
  const month = Number(m[1]);
  const day = Number(m[2]);
  if (month < 1 || month > 12) return String(globalDate);
  return `${MONTHS[month - 1]} ${day}`;
}

// Sort key: prefer the ISO `date` instant, else parse MM-DD-YYYY globalDate;
// games without a date sort last.
function dateSortKey(game: any): number {
  if (game?.date) {
    const t = Date.parse(game.date);
    if (!Number.isNaN(t)) return t;
  }
  const m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(String(game?.globalDate || '').trim());
  if (m) return Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return Number.POSITIVE_INFINITY;
}

export interface ScoreTableRow {
  id: string;
  game: any;
  label: string;
  date: string;
  time: string;
  homeName: string;
  awayName: string;
  homeScore: number | null;
  awayScore: number | null;
  location: string;
  isCancelled: boolean;
}

/**
 * Sorted row view-models for the division score table. Orders by date, then
 * competitionMeta round, then gameNumber (stable for ties) so the table reads
 * like a schedule even when games were created out of order.
 */
export function buildScoreTableRows(divisionGames: any[], divisionTeams: any[]): ScoreTableRow[] {
  const games = Array.isArray(divisionGames) ? [...divisionGames] : [];
  games.sort((a, b) => {
    const dk = dateSortKey(a) - dateSortKey(b);
    if (dk !== 0) return dk;
    const ra = a?.competitionMeta?.round ?? Number.POSITIVE_INFINITY;
    const rb = b?.competitionMeta?.round ?? Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    const ga = a?.competitionMeta?.gameNumber ?? Number.POSITIVE_INFINITY;
    const gb = b?.competitionMeta?.gameNumber ?? Number.POSITIVE_INFINITY;
    return ga - gb;
  });

  return games.map((game) => ({
    id: String(game._id),
    game,
    label: gameRowLabel(game),
    date: formatRowDate(game.globalDate),
    time: game.time || '',
    homeName: resolveName(game.homeTeam, divisionTeams, game.homeTeamPlaceholder),
    awayName: resolveName(game.awayTeam, divisionTeams, game.awayTeamPlaceholder),
    homeScore: game.homeScore == null ? null : Number(game.homeScore),
    awayScore: game.awayScore == null ? null : Number(game.awayScore),
    location: game.location || '',
    isCancelled: isGameCancelled(game),
  }));
}
